import { useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Badge,
  Group,
  SegmentedControl,
  Select,
  Textarea,
  Button,
  Text,
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconNote } from '@tabler/icons-react';
import {
  useCreateAnnotation,
  useUpdateAnnotation,
  useDeleteAnnotation,
} from '@/hooks/useChartAnnotations';
import type { ChartAnnotation } from '@/types/chartAnnotation';
import {
  parseParameterKey,
  getParameterConfig,
  getSourceDisplayName,
} from '@/features/analysis/constants';

interface AddAnnotationModalProps {
  opened: boolean;
  onClose: () => void;
  tankId: string | undefined;
  chartContext: {
    parameter: string;
    parameterLabel: string;
    sources?: string[];
    /** Composite keys (e.g. "alkalinity:aquawiz") for combined view series picker */
    series?: string[];
  };
  existingAnnotation?: ChartAnnotation | null;
}

export function AddAnnotationModal({
  opened,
  onClose,
  tankId,
  chartContext,
  existingAnnotation,
}: AddAnnotationModalProps) {
  const createMutation = useCreateAnnotation(tankId);
  const updateMutation = useUpdateAnnotation(tankId);
  const deleteMutation = useDeleteAnnotation(tankId);

  const form = useForm({
    initialValues: {
      dateRange: [null, null] as [Date | null, Date | null],
      scope: 'parameter' as 'parameter' | 'source',
      source: null as string | null,
      selectedSeries: null as string | null,
      note: '',
    },
    validate: {
      dateRange: (value) => {
        if (!value[0] || !value[1]) return 'Date range is required';
        if (value[1] < value[0]) return 'End date cannot be before start date';
        return null;
      },
      selectedSeries: (value) => {
        // Only required when series picker is shown (combined view)
        if (chartContext.series && chartContext.series.length > 0 && !value) {
          return 'Please select which series this note applies to';
        }
        return null;
      },
      note: (value) =>
        value.trim().length === 0 ? 'Note is required' : null,
    },
  });

  useEffect(() => {
    if (opened) {
      if (existingAnnotation) {
        // Reconstruct composite key for series picker if in combined view context
        const compositeKey = existingAnnotation.parameter && existingAnnotation.source
          ? `${existingAnnotation.parameter}:${existingAnnotation.source}`
          : existingAnnotation.parameter || null;
        form.setValues({
          dateRange: [
            new Date(existingAnnotation.start_time),
            new Date(existingAnnotation.end_time),
          ],
          scope: existingAnnotation.source ? 'source' : 'parameter',
          source: existingAnnotation.source,
          selectedSeries: compositeKey,
          note: existingAnnotation.note,
        });
      } else {
        form.reset();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, existingAnnotation]);

  const handleSubmit = form.onSubmit(async (values) => {
    if (!values.dateRange[0] || !values.dateRange[1]) return;

    // Set start to beginning of day (00:00:00)
    const startDate = new Date(values.dateRange[0]);
    startDate.setHours(0, 0, 0, 0);

    // Set end to end of day (23:59:59.999) so the end date is inclusive
    const endDate = new Date(values.dateRange[1]);
    endDate.setHours(23, 59, 59, 999);

    let effectiveParameter: string | null;
    let effectiveSource: string | null;

    if (values.selectedSeries) {
      // Combined view: derive parameter and source from selected series composite key
      const { param, source } = parseParameterKey(values.selectedSeries);
      effectiveParameter = param;
      effectiveSource = source || null;
    } else {
      // Stacked view / single-parameter context
      effectiveParameter = chartContext.parameter || null;
      effectiveSource = values.scope === 'source'
        ? (values.source ?? null)
        : (chartContext.sources?.length === 1 ? chartContext.sources[0] ?? null : null);
    }

    const payload = {
      start_time: startDate.toISOString(),
      end_time: endDate.toISOString(),
      note: values.note.trim(),
      parameter: effectiveParameter,
      source: effectiveSource,
    };

    try {
      if (existingAnnotation) {
        await updateMutation.mutateAsync({
          id: existingAnnotation.id,
          ...payload,
        });
        notifications.show({
          title: 'Annotation updated',
          message: 'Your note has been saved',
          color: 'green',
        });
      } else {
        await createMutation.mutateAsync(payload);
        notifications.show({
          title: 'Annotation created',
          message: 'Your note has been added to the chart',
          color: 'green',
        });
      }
      onClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save annotation',
        color: 'red',
      });
    }
  });

  const handleDelete = async () => {
    if (!existingAnnotation) return;

    // Confirm before deleting
    const confirmed = window.confirm('Are you sure you want to delete this note? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(existingAnnotation.id);
      notifications.show({
        title: 'Annotation deleted',
        message: 'Your note has been removed',
        color: 'green',
      });
      onClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete annotation',
        color: 'red',
      });
    }
  };

  // Disable all buttons when any mutation is in progress
  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const hasSources = chartContext.sources && chartContext.sources.length > 1;

  const hasSeries = chartContext.series && chartContext.series.length > 0;

  const seriesOptions = useMemo(() => {
    if (!chartContext.series) return [];
    return chartContext.series.map((compositeKey) => {
      const { param, source } = parseParameterKey(compositeKey);
      const config = getParameterConfig(param);
      const sourceName = source ? getSourceDisplayName(source) : '';
      const label = source
        ? `${config.label} - ${sourceName} (${config.unit})`
        : `${config.label} (${config.unit})`;
      return { value: compositeKey, label };
    });
  }, [chartContext.series]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconNote size={20} />
          <Text fw={500}>
            {existingAnnotation ? 'Edit Note' : 'Add Note'}
          </Text>
        </Group>
      }
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {hasSeries ? (
            /* Combined view: series picker */
            <Select
              label="Applies to"
              placeholder="Select which series this note applies to"
              data={seriesOptions}
              value={form.values.selectedSeries}
              onChange={(value) => form.setFieldValue('selectedSeries', value)}
              error={form.errors.selectedSeries}
              required
              searchable
            />
          ) : (
            /* Stacked view / single-parameter: show badge + optional source picker */
            <>
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Chart:
                </Text>
                <Badge variant="light" color="blue">
                  {chartContext.parameterLabel}
                </Badge>
              </Group>

              {hasSources && (
                <>
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      Applies to
                    </Text>
                    <SegmentedControl
                      value={form.values.scope}
                      onChange={(value) =>
                        form.setFieldValue('scope', value as 'parameter' | 'source')
                      }
                      data={[
                        {
                          label: `All ${chartContext.parameterLabel} sources`,
                          value: 'parameter',
                        },
                        { label: 'Specific source', value: 'source' },
                      ]}
                      fullWidth
                    />
                  </div>

                  {form.values.scope === 'source' && (
                    <Select
                      label="Source"
                      placeholder="Select source"
                      data={
                        chartContext.sources?.map((s) => ({
                          value: s,
                          label: s.charAt(0).toUpperCase() + s.slice(1),
                        })) || []
                      }
                      value={form.values.source}
                      onChange={(value) => form.setFieldValue('source', value)}
                      required
                    />
                  )}
                </>
              )}
            </>
          )}

          <DatePickerInput
            type="range"
            label="Date Range"
            placeholder="Select date range"
            allowSingleDateInRange
            value={form.values.dateRange}
            onChange={(value: DatesRangeValue) => {
              if (!value) return;
              if (Array.isArray(value)) {
                const [start, end] = value;
                // Safely convert to Date, validating the result
                const toValidDate = (v: Date | string | null): Date | null => {
                  if (!v) return null;
                  const d = v instanceof Date ? v : new Date(v);
                  // Check for Invalid Date
                  return Number.isNaN(d.getTime()) ? null : d;
                };
                form.setFieldValue('dateRange', [toValidDate(start), toValidDate(end)]);
              }
            }}
            error={form.errors.dateRange}
            clearable
            required
          />

          <Textarea
            label="Note"
            placeholder="What happened during this period?"
            minRows={3}
            maxRows={6}
            autosize
            {...form.getInputProps('note')}
            required
          />

          <Group justify={existingAnnotation ? 'space-between' : 'flex-end'} mt="md">
            {existingAnnotation && (
              <Button
                variant="subtle"
                color="red"
                onClick={handleDelete}
                disabled={isMutating}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            )}
            <Group gap="xs">
              <Button variant="subtle" onClick={onClose} disabled={isMutating}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isMutating}
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {existingAnnotation ? 'Save Changes' : 'Add Note'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
